/**
 * Renovar la copia de la caja bajando solo lo que cambió.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  CÓMO FUNCIONA (el porqué largo está en `shared/huellas.py`, en el servidor)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * El servidor reparte las filas —productos o clientes— en 256 grupos por el
 * principio de su id, y le da a cada grupo una huella sacada de lo que la caja
 * recibiría. Aquí se guardan las huellas de la última vez y se comparan con las
 * de ahora: solo se bajan los grupos que no coinciden, y se cambian en la copia
 * por los recién llegados. Sin cambios, no se baja nada.
 *
 * ⚠️ Las huellas se guardan DESPUÉS de guardar las filas. Si algo falla a
 * medias, la próxima vez las huellas viejas no coinciden y se vuelve a bajar lo
 * que faltó: nunca se da por bueno lo que no llegó.
 */

import type { ItemListRow } from "@/lib/types";
import { descargarPaginadoConTope, type PaginaDeLista } from "./descargarPaginado";

export type Huellas = {
  /** `{grupo: huella}`. Un grupo sin filas no sale. */
  grupos: Record<string, string>;
  total: number;
};

/** Lo que se guarda en el aparato para comparar la próxima vez. */
export type HuellasGuardadas = {
  /**
   * De qué copia son: el negocio, la sucursal, quién la bajó. Si cambia, las
   * filas de antes no sirven —la existencia es de otra sucursal, el costo lo
   * veía otra persona— y se baja todo.
   */
  clave: string;
  grupos: Record<string, string>;
};

/** Pasado este número de grupos cambiados, bajarlo todo sale más a cuenta. */
const MUCHOS_GRUPOS = 128;

export function gruposQueCambiaron(
  antes: Record<string, string>,
  ahora: Record<string, string>,
): string[] {
  const todos = new Set([...Object.keys(antes), ...Object.keys(ahora)]);
  return [...todos].filter((g) => antes[g] !== ahora[g]).sort();
}

/** El grupo de un id: sus dos primeros caracteres, como en el servidor. */
export function grupoDe(id: string): string {
  return id.slice(0, 2).toLowerCase();
}

/** Lo de esos grupos se cambia por lo recién bajado; lo demás se queda. */
export function mezclarPorGrupos<T extends { id: string }>(
  locales: readonly T[],
  grupos: readonly string[],
  nuevos: readonly T[],
): T[] {
  const cambian = new Set(grupos);
  return [...locales.filter((f) => !cambian.has(grupoDe(f.id))), ...nuevos];
}

export type ResultadoPorHuellas<T> = {
  filas: T[];
  /** Las que hay que guardar para la próxima vez; `null` = no guardar ninguna. */
  huellas: HuellasGuardadas | null;
  /** Si había más filas que el tope y lo demás se quedó fuera. */
  recortado: boolean;
  /** Cuántas filas se bajaron de verdad. Para las pruebas y para medir. */
  bajadas: number;
};

export async function renovarPorHuellas<T extends { id: string }>({
  pedirHuellas,
  pedirPagina,
  locales,
  guardadas,
  clave,
  porPagina,
  tope = Number.POSITIVE_INFINITY,
}: {
  pedirHuellas: () => Promise<Huellas>;
  /** Una página del listado; con `grupos`, solo la de esos grupos. */
  pedirPagina: (pagina: number, grupos?: string) => Promise<PaginaDeLista<T>>;
  locales: readonly T[];
  guardadas: HuellasGuardadas | null;
  clave: string;
  porPagina: number;
  tope?: number;
}): Promise<ResultadoPorHuellas<T>> {
  const huellas = await pedirHuellas();
  const nuevas: HuellasGuardadas = { clave, grupos: huellas.grupos };

  const todo = async (): Promise<ResultadoPorHuellas<T>> => {
    const { items, recortado } = await descargarPaginadoConTope(
      (pagina) => pedirPagina(pagina),
      { porPagina, tope },
    );
    // Recortada, la copia no es la de las huellas: no se guardan, y la
    // próxima vez se vuelve a bajar entera, como antes.
    return { filas: items, huellas: recortado ? null : nuevas, recortado, bajadas: items.length };
  };

  // Más de lo que cabe: se baja como siempre, con el tope y su aviso.
  if (huellas.total > tope) return todo();

  // Sin huellas de antes, de otra copia, o con la copia vacía cuando no
  // debería estarlo (se borró): todo. Vacía y sin grupos la última vez es un
  // negocio que no tiene ninguno todavía, y ahí no hay nada que bajar.
  const sirveLaCopia =
    guardadas !== null &&
    guardadas.clave === clave &&
    (locales.length > 0 || Object.keys(guardadas.grupos).length === 0);
  if (!sirveLaCopia) return todo();

  const cambiados = gruposQueCambiaron(guardadas.grupos, huellas.grupos);
  if (cambiados.length === 0) {
    return { filas: [...locales], huellas: nuevas, recortado: false, bajadas: 0 };
  }
  if (cambiados.length > MUCHOS_GRUPOS) return todo();

  const { items: nuevos } = await descargarPaginadoConTope(
    (pagina) => pedirPagina(pagina, cambiados.join(",")),
    { porPagina, tope: Number.POSITIVE_INFINITY },
  );
  return {
    filas: mezclarPorGrupos(locales, cambiados, nuevos),
    huellas: nuevas,
    recortado: false,
    bajadas: nuevos.length,
  };
}

const COMPARAR = new Intl.Collator("es").compare;

/**
 * El catálogo en el orden de la caja: el que le dio el dueño, y dentro de eso
 * por nombre.
 *
 * ⚠️ Se ordena AQUÍ, siempre, y no se confía en el orden en que llegó. Al
 * mezclar lo recién bajado con lo que ya estaba, los nuevos quedarían al final;
 * y ordenar solo esa vez haría que la caja cambiara de orden según se
 * hubiera bajado todo o una parte.
 */
export function ordenarCatalogo(items: readonly ItemListRow[]): ItemListRow[] {
  return [...items].sort(
    (a, b) =>
      (a.display_order ?? 100) - (b.display_order ?? 100) ||
      COMPARAR(a.name, b.name) ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}
